import React from "react";
import { Shield, Bell, Tool } from "react-feather";
import { PANEL_ACCENT } from "./CollapsibleCard";

// Priced service lines (Batch164). Every service type of the branch is a row:
// tick to take it, then Qty x Rate = Amount, with the net total underneath.
// GUARDS qty is never typed - it is the total of the guard requirement lines.
// ALARMS qty is the alarms count (fixed once the enquiry is submitted).
// A ticked row without a rate is allowed on an enquiry - the approver prices it.
//
// State is a plain map { [serviceTypeId]: { checked, qty, rate } } of strings,
// owned by the page; the helpers below turn it into the request shape.

const KIND = {
  GUARDS: { icon: Shield, color: PANEL_ACCENT.guards },
  ALARMS: { icon: Bell, color: PANEL_ACCENT.services },
  OTHER: { icon: Tool, color: PANEL_ACCENT.work },
};

const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const blank = (v) => String(v ?? "").trim() === "";

/** Grid state from saved lines (enquiry/order detail `serviceLines`). */
export const linesStateFrom = (serviceLines = []) =>
  Object.fromEntries(
    serviceLines.map((l) => [
      l.serviceTypeId,
      { checked: true, qty: l.qty != null ? String(Number(l.qty)) : "", rate: l.rate != null ? String(Number(l.rate)) : "" },
    ])
  );

export const checkedServiceTypeIds = (state) =>
  Object.entries(state || {}).filter(([, v]) => v.checked).map(([k]) => Number(k));

const qtyFor = (kind, entry, guardsQty) => (kind === "GUARDS" ? guardsQty : blank(entry?.qty) ? null : Number(entry.qty));

/** Amount of one row, or null when it has no rate / qty yet. */
export const lineAmount = (kind, entry, guardsQty) => {
  const qty = qtyFor(kind, entry, guardsQty);
  if (qty == null || blank(entry?.rate)) return null;
  return round2(qty * Number(entry.rate));
};

const checkedRows = (serviceTypes, state) => (serviceTypes || []).filter((s) => state?.[s.serviceTypeId]?.checked);

export const computeLinesSubTotal = (serviceTypes, state, guardsQty) =>
  round2(checkedRows(serviceTypes, state).reduce((sum, s) => sum + (lineAmount(s.serviceKind, state[s.serviceTypeId], guardsQty) || 0), 0));

/** True when every ticked row has an amount (required on approve / direct confirm). */
export const allLinesPriced = (serviceTypes, state, guardsQty) =>
  checkedRows(serviceTypes, state).every((s) => lineAmount(s.serviceKind, state[s.serviceTypeId], guardsQty) != null);

/** The alarms count the grid implies (0 when Alarms is not ticked). */
export const alarmsQtyFrom = (serviceTypes, state) => {
  const alarms = checkedRows(serviceTypes, state).find((s) => s.serviceKind === "ALARMS");
  return alarms ? Number(state[alarms.serviceTypeId].qty) || 0 : 0;
};

export const hasCheckedKind = (serviceTypes, state, kind) => checkedRows(serviceTypes, state).some((s) => s.serviceKind === kind);

/** Wire shape: ServiceLineRequest(serviceTypeId, qty, rate) for each ticked row. */
export const toServiceLineRequests = (serviceTypes, state, guardsQty) =>
  checkedRows(serviceTypes, state).map((s) => {
    const entry = state[s.serviceTypeId];
    return {
      serviceTypeId: s.serviceTypeId,
      qty: qtyFor(s.serviceKind, entry, guardsQty),
      rate: blank(entry.rate) ? null : Number(entry.rate),
    };
  });

/** First problem with the lines, or null. `requireRates` for approve / direct confirm;
 *  `requireGuardLines` off at approval, where the guard lines are already fixed. */
export const validateServiceLines = (serviceTypes, state, guardsQty, { requireRates = false, requireGuardLines = true } = {}) => {
  const rows = checkedRows(serviceTypes, state);
  if (rows.length === 0) return "Select at least one service";
  for (const s of rows) {
    const e = state[s.serviceTypeId];
    if (!blank(e.rate) && Number(e.rate) < 0) return `The rate for ${s.serviceTypeName} cannot be negative`;
    if (s.serviceKind === "ALARMS" && !blank(e.qty) && !Number.isInteger(Number(e.qty))) return "The alarms quantity must be a whole number";
    if (s.serviceKind !== "GUARDS" && !blank(e.qty) && Number(e.qty) < 0) return `The quantity for ${s.serviceTypeName} cannot be negative`;
    if (s.serviceKind === "OTHER" && !blank(e.rate) && !(Number(e.qty) > 0)) return `Enter a quantity for ${s.serviceTypeName}`;
    if (requireGuardLines && s.serviceKind === "GUARDS" && guardsQty <= 0) return "Add the guard requirement lines for the Guards service";
    if (requireRates && lineAmount(s.serviceKind, e, guardsQty) == null) return `Enter the quantity and rate for ${s.serviceTypeName}`;
  }
  return null;
};

const ServiceLinesGrid = ({
  serviceTypes = [],
  value = {},
  onChange,
  guardsQty = 0,
  alarmsQtyLocked = false,
  selectionLocked = false,
  disabled = false,
  emptyHint,
}) => {
  const patch = (id, change) => {
    const current = value[id] || { checked: false, qty: "", rate: "" };
    onChange({ ...value, [id]: { ...current, ...change } });
  };

  const toggle = (s) => {
    const current = value[s.serviceTypeId];
    const checked = !current?.checked;
    // A freshly ticked Alarms / Other row starts at 1 so the amount shows as soon as a rate is typed.
    const qty = checked && s.serviceKind !== "GUARDS" && blank(current?.qty) ? "1" : current?.qty ?? "";
    patch(s.serviceTypeId, { checked, qty });
  };

  const subTotal = computeLinesSubTotal(serviceTypes, value, guardsQty);
  const pickedCount = checkedServiceTypeIds(value).filter((id) => serviceTypes.some((s) => s.serviceTypeId === id)).length;

  if (serviceTypes.length === 0) {
    return <div className="text-muted small py-2">{emptyHint || "No service types to show."}</div>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle mb-0" style={{ minWidth: 560 }}>
        <thead>
          <tr style={{ background: "#F8F9FB" }}>
            <th style={{ width: 36 }} />
            <th>Service</th>
            <th style={{ width: 120 }} className="text-end">Qty</th>
            <th style={{ width: 150 }} className="text-end">Rate</th>
            <th style={{ width: 150 }} className="text-end">Amount</th>
          </tr>
        </thead>
        <tbody>
          {serviceTypes.map((s) => {
            const entry = value[s.serviceTypeId] || { checked: false, qty: "", rate: "" };
            const k = KIND[s.serviceKind] || KIND.OTHER;
            const Icon = k.icon;
            const on = !!entry.checked;
            const amount = on ? lineAmount(s.serviceKind, entry, guardsQty) : null;
            const qtyReadOnly = s.serviceKind === "GUARDS" || (s.serviceKind === "ALARMS" && alarmsQtyLocked);
            return (
              <tr
                key={s.serviceTypeId}
                style={{
                  opacity: on ? 1 : 0.55,
                  background: on ? `${k.color}0D` : "transparent",
                  borderLeft: `3px solid ${on ? k.color : "transparent"}`,
                }}
              >
                <td>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={on}
                    disabled={disabled || selectionLocked}
                    onChange={() => toggle(s)}
                    aria-label={`Take ${s.serviceTypeName}`}
                  />
                </td>
                <td>
                  <span className="d-inline-flex align-items-center gap-2 fw-semibold" style={{ color: on ? "#1D2939" : undefined }}>
                    <span
                      className="d-inline-flex align-items-center justify-content-center rounded-circle"
                      style={{ width: 24, height: 24, background: `${k.color}1F`, color: k.color }}
                    >
                      <Icon size={13} />
                    </span>
                    {s.serviceTypeName}
                  </span>
                </td>
                <td className="text-end">
                  {qtyReadOnly ? (
                    <span title={s.serviceKind === "GUARDS" ? "Total of the guard requirement lines" : "Fixed at the enquiry's alarms count"}>
                      <span className="badge rounded-pill" style={{ background: on ? k.color : "#C0C6CF", color: "#fff", minWidth: 32 }}>
                        {s.serviceKind === "GUARDS" ? guardsQty : entry.qty || 0}
                      </span>
                      {s.serviceKind === "GUARDS" && <div className="text-muted" style={{ fontSize: 10.5 }}>from guard lines</div>}
                    </span>
                  ) : (
                    <input
                      type="number"
                      min="0"
                      step={s.serviceKind === "ALARMS" ? 1 : "any"}
                      className="form-control form-control-sm text-end"
                      value={entry.qty}
                      disabled={disabled || !on}
                      onChange={(e) => patch(s.serviceTypeId, { qty: e.target.value })}
                    />
                  )}
                </td>
                <td className="text-end">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control form-control-sm text-end"
                    placeholder={on ? "0.00" : ""}
                    value={entry.rate}
                    disabled={disabled || !on}
                    onChange={(e) => patch(s.serviceTypeId, { rate: e.target.value })}
                  />
                </td>
                <td className="text-end fw-semibold">
                  {on ? (amount != null ? money(amount) : <span className="text-muted fw-normal small">not priced</span>) : ""}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: `2px solid ${PANEL_ACCENT.money}` }}>
            <td />
            <td className="fw-semibold text-muted small">
              {pickedCount} service{pickedCount === 1 ? "" : "s"} selected
            </td>
            <td colSpan={2} className="text-end fw-semibold">Net Total</td>
            <td className="text-end fw-bold" style={{ color: PANEL_ACCENT.money, fontSize: 15 }}>{money(subTotal)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

/** True when saved lines carry at least one amount (Batch164 onwards / backfilled). */
export const hasPricedLines = (serviceLines) => (serviceLines || []).some((l) => l.amount != null);

/** Read-only priced lines for the Enquiry / Order detail panels. */
export const ServiceLinesTable = ({ lines = [] }) => {
  const total = round2(lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0));
  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle mb-0">
        <thead>
          <tr>
            <th>Service</th>
            <th className="text-end" style={{ width: 90 }}>Qty</th>
            <th className="text-end" style={{ width: 130 }}>Rate</th>
            <th className="text-end" style={{ width: 140 }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => {
            const k = KIND[l.serviceKind] || KIND.OTHER;
            const Icon = k.icon;
            return (
              <tr key={l.serviceTypeId} style={{ borderLeft: `3px solid ${k.color}` }}>
                <td>
                  <span className="d-inline-flex align-items-center gap-2 fw-semibold">
                    <span
                      className="d-inline-flex align-items-center justify-content-center rounded-circle"
                      style={{ width: 22, height: 22, background: `${k.color}1F`, color: k.color }}
                    >
                      <Icon size={12} />
                    </span>
                    {l.serviceTypeName}
                  </span>
                </td>
                <td className="text-end">{l.qty != null ? Number(l.qty) : "-"}</td>
                <td className="text-end">{l.rate != null ? money(l.rate) : "-"}</td>
                <td className="text-end fw-semibold">
                  {l.amount != null ? money(l.amount) : <span className="text-muted fw-normal small">not priced</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: `2px solid ${PANEL_ACCENT.money}` }}>
            <td colSpan={3} className="text-end fw-semibold">Net Total</td>
            <td className="text-end fw-bold" style={{ color: PANEL_ACCENT.money }}>{money(total)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default ServiceLinesGrid;
